import React, { useEffect, useState } from "react";
import * as yup from "yup";
import { FaFolder } from "react-icons/fa";
import { Radio, Table } from "antd";
import moment from 'moment';
import styles from "../../styles.module.scss";
import { Flex } from "@components/ui/Flex";
import { ShowWhenTrue } from "@helpers/showwhentrue";
import { Text } from "@components/ui/Text";
import { ModelRequestType, RootType, formTitle } from "./constant";
import { isEmpty } from "lodash";
import HierarchyReqForm from "./Forms/ReqForm";
import HierarchySinkForm from "./Forms/SinkForm";
import HierarchyScheduleForm from "./Forms/ScheduleForm";
import { DataSourceHandler } from "@api/data-source-handler";
import { useSelector } from "react-redux";
import { RootState } from "@store/types";
import { errorAlert, successAlert } from "@components/toastify/notify-toast";
import { errorHandlerFor400 } from "@api/services/errorhandler";
import { TableMetaData } from "@pages/data-browser/treeviews/connections-treeview";
import { _canvasdagFormInitialValues, executionTimeFrequencyMultiplier } from "@pages/workflow-page/utils";
import { uuid } from "uuidv4";
import { ScheduleTiming, formatScheduleStartDate } from "@pages/workflow-page/modals/schedule-workflow";

interface HierarchyViewProps {
  formValuesForEdit: ModelRequestType | null;
  selectedElements: RootType;
  setFormValuesForEdit: (formValues: ModelRequestType | null) => void;
  tableDataSources: any;
  columns: any[];
  isLoadingElements: boolean;
  showForm: boolean;
  activeStep: number;
  activeRecordsMetadata: TableMetaData,
  selectedRowKeys: any[];
  setActiveStep: (step: number) => void;
  setShowForm: (show: boolean) => void;
  setSelectedRowKeys: (keys: any[]) => void;
  setIsLoadingElements: (loading: boolean) => void;
  handleTableChange: (pagination: any, filters: any, sorter: any, extra: { action: string; currentDataSource: any; }) => any;
  handleGoToReqPage: () => void;
  tagPosition: string;
  setTagPosition: (position: string) => void;
}

const formSchemaReqForm = yup.object().shape({
  requestName: yup
    .string()
    .required("This is a required field")
    .matches(/^[A-Za-z0-9_-]+$/, "Special Characters are not allowed except _ and -"),
  elementRootPath: yup.string(),
  requestType: yup.string().required("This is a required field"),
  elementPath: yup.string(),
  output_format: yup.string(),
  all_descendants: yup.boolean(),
  tags: yup.string().required("This is a required field"),
  start_time_str: yup.mixed().when('requestType', {
    is: (value) => value !== 'real_time',
    then: yup.mixed().required('This is a required field')
      .test("is-not-empty", "Start time cannot be empty", (value) => {
        return value !== null && value !== undefined && value !== "";
      })
      .test("is-past-date", "Start time must be in the past", (value) => {
        return value ? moment.utc(value).isBefore(moment.utc()) : false;
      }),
  }),
  end_time_str: yup.mixed().when('requestType', {
    is: (value) => value !== 'real_time',
    then: yup.mixed().required('This is a required field')
      .test("is-not-empty", "End time cannot be empty", (value) => {
        return value !== null && value !== undefined && value !== "";
      })
      .test("is-valid-end-time", "End time must be after start time", function (value) {
        const { start_time_str } = this.parent;
        return value && start_time_str ? moment.utc(value).isAfter(moment.utc(start_time_str)) : false;
      })
      .test("is-before-now", "End time must be before the current time", (value) => {
        return value ? moment.utc(value).isBefore(moment.utc()) : false;
      }),
  }),
  frequency: yup.string().when('requestType', {
    is: 'interpolated',
    then: yup.string().required('This is a required field'),
  }),
  frequencyType: yup.string().when('requestType', {
    is: 'interpolated',
    then: yup.string().required('This is a required field'),
  }),
  realtime_values: yup.string().when('requestType', { is: 'real_time', then: yup.string().required('This is a required field') }),
  period_ms: yup.string().when('realtime_values', { is: 'live', then: yup.string().required('This is a required field') }),
});

const formSchemaSinkForm = yup.object().shape({
  sinkKind: yup
    .string()
    .required("This is a required field"),
  sinkName: yup
    .string()
    .required("This is a required field"),
});

const extractPaths = (input: string) => {
  const parts = input.split("\\").filter(Boolean);
  if (parts.length === 0) {
    return { rootPath: "", elementPath: "" };
  }
  const rootPath = parts.slice(0, -1).join("\\");
  const elementPath = parts[parts.length - 1];

  return { rootPath, elementPath };
};

const updateTimespan = (timespan: string, frequency: number, frequencyType: string): string => {
  const parts = timespan.split(":").map(Number);
  switch (frequencyType.toLowerCase()) {
    case "seconds":
      parts[2] = frequency;
      break;
    case "minutes":
      parts[1] = frequency;
      break;
    case "hours":
      parts[0] = frequency;
      break;
    default:
      console.warn("Invalid frequencyType");
  }
  return parts.map((num) => num.toString().padStart(2, "0")).join(":");
};

const HierarchyView: React.FC<HierarchyViewProps> = ({
  formValuesForEdit,
  setFormValuesForEdit,
  selectedElements,
  tableDataSources,
  columns,
  isLoadingElements,
  showForm,
  activeStep,
  selectedRowKeys,
  setSelectedRowKeys,
  activeRecordsMetadata,
  setIsLoadingElements,
  setActiveStep,
  setShowForm,
  handleTableChange,
  handleGoToReqPage,
  tagPosition,
  setTagPosition
}) => {
  const requestJson = JSON.parse(formValuesForEdit?.requestJson || `{}`);
  const elementRootPath = requestJson?.element_root;
  const elementPathName = requestJson?.element_name;
  const { rootPath, elementPath } = extractPaths(selectedElements?.path || "");
  const { envVariables: Env } = useSelector((store: RootState) => store.AccountReducer);
  const intialValuesReqForm = { requestName: "", start_time_str: null, end_time_str: null, tags: selectedRowKeys.join(","), elementRootPath: rootPath, elementPath: elementPath, requestType: "raw", realtime_values: 'current', frequency: "1", frequencyType: "seconds", period_ms: '',all_descendants:requestJson?.all_descendants,output_format:"csv" };
  const intialValuesSinkForm = { sinkKind: 'S3', sinkName: "", region: '', accessKeyId: '', secretAccessKey: '', database: '', mode: '', schema: '', warehouse: '', role: '' };
  const workSpaceData = useSelector((store: RootState) => store.ClusterReducer.workspaceList);
  const enabledWorkspace = workSpaceData?.filter((item) => item.isEnabled)?.[0];
  const cluster = useSelector(
    (store: RootState) => store.ClusterReducer.defaultClusterInfo
  );

  const [intialValuesReqFormState, setIntialValuesReqFormState] = useState({} as any);
  const [intialValuesSinkFormState, setIntialValuesSinkFormState] = useState({} as any);
  const [intialValuesScheduleFormState, setIntialValuesScheduleFormState] = useState({} as any);

  const handleSubmitReqForm = (values: any) => {
    setIntialValuesReqFormState(values);
    if (values?.requestType === "real_time") {
      setIntialValuesScheduleFormState({ ..._canvasdagFormInitialValues, scheduleTimingInfo: ScheduleTiming["Run Once"] });
    }
    setActiveStep(activeStep + 1);
  }

  const handleSubmitSinkForm = (values: any) => {
    setIntialValuesSinkFormState(values);
    setActiveStep(activeStep + 1);
  }

  useEffect(() => {
    if (isEmpty(formValuesForEdit)) {
      setIntialValuesReqFormState(intialValuesReqForm);
      setIntialValuesSinkFormState(intialValuesSinkForm);
      setIntialValuesScheduleFormState(_canvasdagFormInitialValues);
    }
    else {
      const reqFromInitialValues = {
        requestName: formValuesForEdit?.modelRequestName,
        start_time_str: moment.utc(requestJson?.start_time_str).format('YYYY-MM-DD HH:mm:ss.SSS'),
        end_time_str: moment.utc(requestJson?.end_time_str).format('YYYY-MM-DD HH:mm:ss.SSS'),
        tags: selectedRowKeys?.toString(),
        elementRootPath: isEmpty(selectedRowKeys)?requestJson?.element_root: rootPath,
        elementPath:  isEmpty(selectedRowKeys)?requestJson?.element_name: elementPath,
        all_descendants: requestJson?.all_descendants,
        requestType: formValuesForEdit?.requestType,
        realtime_values: formValuesForEdit?.realtime_values,
        frequency: formValuesForEdit?.frequency,
        frequencyType: formValuesForEdit?.frequencyType,
        period_ms: formValuesForEdit?.period_ms,
        output_format: requestJson?.output_format,
      }
      setIntialValuesReqFormState(reqFromInitialValues);
      setIntialValuesSinkFormState({ sinkKind: formValuesForEdit?.sinkKind, sinkName: formValuesForEdit?.sinkName });
      setIntialValuesScheduleFormState({ ..._canvasdagFormInitialValues,scheduleTimingInfo:formValuesForEdit?.dagGenDto?.schedule_interval==="@once"?ScheduleTiming["Run Once"]:ScheduleTiming["Recurring"] });
    }
  }, [selectedRowKeys, selectedElements, formValuesForEdit]);

  const handleSubmitScheduleForm = (values: any) => {
    setIntialValuesScheduleFormState(values);
    handleOnPublish(values);
  };

  const handleOnPublish = (values: any) => {
    const commandReqTypeRef = {
      "raw": "get_historical_data",
      "real_time": "get_current_data",
      "interpolated": "get_interpolated_data"
    }
    setIsLoadingElements(true);
    if (!(!isEmpty(enabledWorkspace) && enabledWorkspace?.isEnabled && cluster && cluster.defaultCluster)) {
      errorAlert("Cluster should be the default cluster and workspace should be enabled");
      setIsLoadingElements(false);
      return;
    }
    const get_command = intialValuesReqFormState?.requestType === "real_time" ? (intialValuesReqFormState?.realtime_values === "live" ? "get_live_data" : "get_current_data") : commandReqTypeRef[intialValuesReqFormState?.requestType as keyof typeof commandReqTypeRef];
    const payload = {
      serverName: activeRecordsMetadata?.afStructureDto?.serverName,
      dataBaseName: activeRecordsMetadata?.afStructureDto?.databaseName,
      tagSelections: selectedRowKeys,
      requestType: intialValuesReqFormState?.requestType,
      realtime_values: intialValuesReqFormState?.realtime_values || "current",
      frequency: intialValuesReqFormState?.frequency,
      frequencyType: intialValuesReqFormState?.frequencyType,
      period_ms: intialValuesReqFormState?.period_ms,
      tagSelectedType: tagPosition,
      requestJson: JSON.stringify({
        tags: intialValuesReqFormState?.tags,
        command: get_command,
        node_name: `${activeRecordsMetadata.managedNodeInfo?.nodeName}`,
        fullpath: true,
        server_type: "PI_AF",
        db_name: activeRecordsMetadata?.afStructureDto?.databaseName,
        element_name: intialValuesReqFormState?.elementPath,
        element_root: intialValuesReqFormState?.elementRootPath,
        all_descendants: intialValuesReqFormState?.all_descendants,
        ...(intialValuesReqFormState?.requestType === "interpolated" && {
          timespan: updateTimespan(
            "00:00:00",
            parseInt(intialValuesReqFormState?.frequency),
            intialValuesReqFormState?.frequencyType
          ),
        }),
        ...(intialValuesReqFormState?.requestType !== "real_time" && {
          start_time_str: moment
            .utc(intialValuesReqFormState?.start_time_str)
            .format("YYYY-MM-DD HH:mm:ss.SSS"),
          end_time_str: moment
            .utc(intialValuesReqFormState?.end_time_str)
            .format("YYYY-MM-DD HH:mm:ss.SSS"),
        }),
        ...(intialValuesReqFormState?.requestType === "real_time" &&
          intialValuesReqFormState?.realtime_values === "live" && {
          period_ms: Number(intialValuesReqFormState?.period_ms),
        }),
        output_format: intialValuesReqFormState?.output_format
      }),
      nodeName: activeRecordsMetadata.managedNodeInfo?.nodeName,
      dagGenDto: {
        dag_id: isEmpty(formValuesForEdit)?"model-request-" + uuid(): values.scheduleTimingInfo === ScheduleTiming['Run Once']?"model-request-" + uuid():formValuesForEdit?.dagGenDto?.dag_id,
        description: values?.description || "",
        schedule_interval: values.scheduleTimingInfo === ScheduleTiming.None
          ? 'None'
          : (values.scheduleTimingInfo === ScheduleTiming.Recurring
            ? values.cronExpression
            : "@once"),
        start_date: values.scheduleTimingInfo === ScheduleTiming.Recurring ? values.startDate:values.scheduleTimingInfo === ScheduleTiming['Run Once'] ? values.formattedRunOnceExecutionDate: formatScheduleStartDate(moment().utc()) ,
        isDatabricks: Env?.REACT_APP_DATABRICKS,
        scheduleType: values?.scheduleType,
        default_args: {
          owner: "Airflow",
          depends_on_past: false,
          email: values?.overrideNotificationEmail ? [values?.notificationEmails] : [],
          email_on_failure: values?.overrideNotificationEmail,
          email_on_retry: false,
          retries: values?.scheduleAdvancedMode ? parseInt(values.retries as unknown as string) : 0,
          retry_delay: values?.scheduleAdvancedMode ? parseInt(values.retryDelay as unknown as string) : 0,
          execution_timeout: parseInt(values.execution_timeout as unknown as string) *
            (executionTimeFrequencyMultiplier[
              values.execution_timeout_frequency as keyof typeof executionTimeFrequencyMultiplier
            ] || 1)
        },
        nodes: [
          {
            id: "0099f361-f877-4600-85f4-58b7445bc143",
            fn_id: "DatabricksSubmitRunOperator",
            fnName: "DatabricksSubmitRunOperator",
            title: "Databricks Operator",
            inputPorts: [],
            outputPorts: [
              {
                id: "4eb20487-942d-48b9-aea0-7b32d94c58c4",
                type: "OUT-0",
                link: [],
                parentNode: "0099f361-f877-4600-85f4-58b7445bc143"
              }
            ],
            data: [
              { key: "task_id", value: "task_id_" + uuid(), arg_index: 1, variable_type: "string", useKey: true },
              { key: "existing_workflow", value: true, arg_index: 6, variable_type: "boolean", useKey: true },
              { key: "workflow_id", value:Env?.REACT_APP_PI_INTEGRATOR_WORKFLOW_ID, arg_index: 2, variable_type: "integer", useKey: true },
              { key: "databricks_conn_id", value: enabledWorkspace?.workspaceName, arg_index: 3, variable_type: "string", useKey: true },
              { key: "notebook_task", value: "ssh_spark", arg_index: 4, variable_type: "string", useKey: true },
              { key: "existing_cluster_id", value: cluster?.databricksClusterId, arg_index: 5, variable_type: "string", useKey: true },
              { key: "base_parameters", value: "", arg_index: 8, variable_type: "string", useKey: true },
              { key: "databrick_file_path", value: "", arg_index: 7, variable_type: "string", useKey: true }
            ],
            type: "component",
            df_name: "databricks_submit_run_operator_1",
            previewAllowed: true,
            field_workflow_config_mapping: {},
            field_var_mapping: [],
            extraData: {}
          }
        ],
        links: [],
        workspaceType: enabledWorkspace?.workspaceType,
        max_active_runs: values?.max_active_runs,
        catchup: false,
        exponential_backoff: false
      },
      sinkKind: intialValuesSinkFormState?.sinkKind,
      sinkName: intialValuesSinkFormState?.sinkName,
      modelRequestName: intialValuesReqFormState?.requestName
    };

    if (isEmpty(formValuesForEdit)) {
      DataSourceHandler.PublishHierarchyRequest(
        `${Env?.REACT_APP_PLATFORM_URL}/databrowser/api`,
        payload,
        (response: any) => {
          setIsLoadingElements(false);
          handleGoToReqPage();
          setSelectedRowKeys([]);
          setFormValuesForEdit(null);
          successAlert("Request has been successfully submitted");
        },
        (error: any) => {
          setIsLoadingElements(false);
          errorHandlerFor400(error);
          setSelectedRowKeys([]);
          setFormValuesForEdit(null);
        }
      );
    } else {
      DataSourceHandler.UpdateModelRequest(
        `${Env?.REACT_APP_PLATFORM_URL}/databrowser/api`,
        String(formValuesForEdit?.id),
        payload,
        (response: any) => {
          setIsLoadingElements(false);
          handleGoToReqPage();
          setFormValuesForEdit(null);
          successAlert("Request has been successfully updated");
        },
        (error: any) => {
          setIsLoadingElements(false);
          errorHandlerFor400(error);
          setFormValuesForEdit(null);
        }
      );
    }

  }

  const startEndTimePayloadString = intialValuesReqFormState?.start_time_str && intialValuesReqFormState?.end_time_str && `start=${moment
    .utc(intialValuesReqFormState?.start_time_str)
    .format("YYYY-MM-DD HH:mm:ss")}&end=${moment
    .utc(intialValuesReqFormState?.end_time_str)
    .format("YYYY-MM-DD HH:mm:ss")}`;

  return (
    <Flex className={styles.boxHierarchyView} flexDirection="column" gap="0px">
      <div className={styles.labelHierarchyView}>
        <Flex gap="8px" alignItems={"center"}>
          <FaFolder />
          <Text fontSize="14px" fontWeight={600}>{formTitle[activeStep]}</Text>
        </Flex>
        <ShowWhenTrue show={activeStep === 0}>
          <Flex gap="10px" justifyContent={"end"} alignItems={"center"}>
            <Radio.Group className={"custom-radio-group-tag-position"} value={tagPosition} onChange={(e) => {
              if (isEmpty(formValuesForEdit)) {
                setSelectedRowKeys([]);
              }
              setTagPosition(e.target.value)
            }} style={{ display: 'flex' }}>
              <Radio.Button value="Elements">Attributes</Radio.Button>
              {/* <Radio.Button value="Pipoint">PIPoint</Radio.Button> */}
            </Radio.Group>
          </Flex>
        </ShowWhenTrue>
      </div>
      <div>
        <ShowWhenTrue show={!showForm}>
          <Table
            dataSource={tableDataSources}
            onChange={handleTableChange as any}
            loading={isLoadingElements}
            columns={columns}
            size="small"
            className={styles.dataTableHierarchy}
            pagination={false}
            locale={{
              emptyText: <Text color="white" fontSize={14}>{isEmpty(formValuesForEdit) ? "No data" : `Please select the ${elementRootPath}\\${elementPathName} path to retrieve the previously selected tags.`}</Text>,
            }}
          />
        </ShowWhenTrue>
        <ShowWhenTrue show={showForm && activeStep > 0}>
          <ShowWhenTrue show={activeStep === 1}>
            <HierarchyReqForm
              activeStep={activeStep}
              setActiveStep={setActiveStep}
              initialValues={intialValuesReqFormState}
              formSchema={formSchemaReqForm}
              selectedRowKeys={selectedRowKeys}
              setShowForm={setShowForm}
              handleOnSubmit={handleSubmitReqForm} />
          </ShowWhenTrue>
          <ShowWhenTrue show={activeStep === 2}>
            <HierarchySinkForm
              activeStep={activeStep}
              setActiveStep={setActiveStep}
              initialValues={intialValuesSinkFormState}
              formSchema={formSchemaSinkForm}
              handleOnSubmit={handleSubmitSinkForm} />
          </ShowWhenTrue>
          <ShowWhenTrue show={activeStep === 3}>
            <HierarchyScheduleForm
              activeStep={activeStep}
              setActiveStep={setActiveStep}
              intialValuesScheduleFormState={intialValuesScheduleFormState}
              handleOnSubmit={handleSubmitScheduleForm}
              isLoadingElements={isLoadingElements}
              isEditForm={!isEmpty(formValuesForEdit) ? true : false}
              startEndTimePayloadString={startEndTimePayloadString}
              isDisableRequrring={intialValuesReqFormState?.requestType === "real_time" ? true : false}
              selectedScheduleTimingInfo={formValuesForEdit?.dagGenDto?.schedule_interval||""}
            />
          </ShowWhenTrue>
        </ShowWhenTrue>

        <ShowWhenTrue show={activeStep < 1}>
          <div className={styles.hierarchyTableFooter}>
            <button
              className="btn btn-yellow btn-md"
              onClick={() => {
                if(!isEmpty(selectedRowKeys)){
                  setShowForm(true);
                  setActiveStep(activeStep + 1);
                }else{
                  errorAlert("Please select the tags to proceed");
                }
              }}
            >
              Next
            </button>
          </div>
        </ShowWhenTrue>
      </div>
    </Flex>
  );
};

export default HierarchyView;
